<template>
  <div class="model-management">
    <!-- 标题 + 按钮 -->
    <div class="header-bar">
      <div class="header-title">接入型号授权列表</div>
      <a-button type="primary" icon="plus" @click="handleAdd">添加设备型号</a-button>
    </div>

    <!-- 表格 -->
    <a-table
      :columns="columns"
      :data-source="modelList"
      :rowKey="(record) => record.id"
      :pagination="pagination"
      @change="handleTableChange"
      :loading="loading"
      :scroll="{ x: 1100 }"
      size="middle"
    >
      <span slot="index" slot-scope="text, record, idx">{{ (idx + 1) + (pagination.current - 1) * pagination.pageSize }}</span>

      <span slot="modelInfo" slot-scope="text, record">
        <div class="model-info-cell">
          <div class="model-id mono-text">{{ record.typeName || '--' }}</div>
          <div class="model-name">{{ record.typeDescription || '--' }}</div>
        </div>
      </span>

      <span slot="seriesName" slot-scope="text, record">
        <a-tag v-if="record.seriesName" color="blue">
          {{ record.seriesName }}
        </a-tag>
        <span v-else>--</span>
      </span>

      <span slot="tenantInfo" slot-scope="text, record">
        <div class="tenant-info-cell">
          <div class="tenant-name">{{ record.tenantName || '--' }}</div>
          <div class="tenant-id mono-text">{{ record.tenantId || '--' }}</div>
        </div>
      </span>

      <span slot="authorizedCount" slot-scope="text">{{ formatNumber(text) || 0 }}</span>

      <span slot="authorizedAt" slot-scope="text">
        {{ text ? $moment(text).format('YYYY-MM-DD HH:mm') : '--' }}
      </span>

      <span slot="operator" slot-scope="text">{{ text || '--' }}</span>

      <span slot="action" slot-scope="text, record">
        <!-- <a-button type="link" size="small" @click="handleConfigFile(record)">配置文件</a-button> -->
        <!-- <a-divider type="vertical" /> -->
        <a-button type="link" size="small" class="remove-btn" @click="handleRemove(record)">移除授权</a-button>
      </span>
    </a-table>

    <!-- 添加弹窗 -->
    <add-model
      :visible="addVisible"
      :client-id="clientId"
      @close="addVisible = false"
      @confirm="handleAddConfirm"
    />

    <!-- 移除弹窗 -->
    <remove-model
      :visible="removeVisible"
      :record="currentRemoveRecord"
      @close="removeVisible = false"
      @confirm="handleRemoveConfirm"
    />
  </div>
</template>

<script>
import AddModel from './modal/AddModel.vue'
import RemoveModel from './modal/RemoveModel.vue'
import { DEVICE_SERIES } from '../clientConstants.js'

const columns = [
  { title: '序号', key: 'index', scopedSlots: { customRender: 'index' }, width: 60, align: 'center' },
  { title: '型号 / 型号描述', key: 'modelInfo', scopedSlots: { customRender: 'modelInfo' }, width: 180 },
  { title: '所属系列', dataIndex: 'seriesName', key: 'seriesName', scopedSlots: { customRender: 'seriesName' }, width: 120, align: 'center' },
  { title: '租户名称 / 租户ID', key: 'tenantInfo', scopedSlots: { customRender: 'tenantInfo' }, width: 200 },
  { title: '授权设备数', dataIndex: 'authorizedCount', key: 'authorizedCount', scopedSlots: { customRender: 'authorizedCount' }, width: 120, align: 'left' },
  { title: '授权时间', dataIndex: 'authorizedAt', key: 'authorizedAt', scopedSlots: { customRender: 'authorizedAt' }, width: 120 },
  { title: '操作人', dataIndex: 'operator', key: 'operator', scopedSlots: { customRender: 'operator' }, width: 150 },
  { title: '操作', key: 'action', scopedSlots: { customRender: 'action' }, width: 160, fixed: 'right', align: 'center' },
]

export default {
  name: 'ModelManagement',
  components: { AddModel, RemoveModel },
  props: {
    clientId: { type: String, default: '' },
  },
  data() {
    return {
      columns,
      modelList: [],
      loading: false,
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条`,
        pageSizeOptions: ['10', '20', '30', '50'],
      },
      addVisible: false,
      removeVisible: false,
      currentRemoveRecord: null,
    }
  },
  created() {
    this.fetchList()
  },
  watch: {
    clientId() {
      if (this.clientId) this.fetchList()
    },
  },
  methods: {
    /* 加载列表 */
    fetchList() {
      if (!this.clientId) return
      this.loading = true
      const params = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        keyword: null,
      }
      this.$api.getClientModels(this.clientId, params).then((res) => {
        if (res && res.code == 0) {
          this.loading = false
          this.modelList = res.data.records || []
          this.pagination.total = Number(res.data.total || 0)
        }
      }).catch((err) => {
        console.error(err)
      }).finally(() => {
        this.loading = false
      })
    },

    handleTableChange({ pageSize, current }) {
      this.pagination.pageSize = pageSize
      this.pagination.current = current
      this.fetchList()
    },

    /* 添加 */
    handleAdd() {
      this.addVisible = true
    },
    handleAddConfirm({ typeIds, tenantId }) {
      this.$api.addClientModel(this.clientId, { typeIds, tenantId }).then((res) => {
        if (res && res.code == 0) {
          this.$message.success('授权成功')
          this.addVisible = false
          this.fetchList()
        } else {
          this.$message.error(res && res.message ? res.message : '授权失败')
        }
      }).catch(() => {
        this.$message.success('授权成功（模拟）')
        this.addVisible = false
        this.fetchList()
      })
    },

    /* 配置文件 */
    handleConfigFile(record) {
      this.$message.info(`查看型号「${record.name}」的配置文件（后续开发）`)
    },

    /* 移除授权 */
    handleRemove(record) {
      this.currentRemoveRecord = record
      this.removeVisible = true
    },
    handleRemoveConfirm(done) {
      this.$api.removeClientModel(this.clientId, this.currentRemoveRecord.typeId).then((res) => {
        done && done()
        if (res && res.code == 0) {
          this.$message.success('已移除授权')
          this.removeVisible = false
          this.fetchList()
        } else {
          this.$message.error('移除失败')
        }
      }).catch(() => {
        done && done()
        this.removeVisible = false
      })
    },

    /* 工具 */
    getSeriesLabel(value) {
      const t = DEVICE_SERIES.find((i) => i.value === value)
      return t ? t.label : value
    },

    formatNumber(val) {
      if (!val) return 0
      return Number(val).toLocaleString()
    },
  },
}
</script>

<style lang="less" scoped>
.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;

  .header-title {
    font-size: 15px;
    font-weight: 600;
    color: #151515;
    padding-left: 10px;
    border-left: 3px solid #1890ff;
  }
}

.model-info-cell, .tenant-info-cell {
  .model-id, .tenant-id {
    font-size: 12px;
    color: #999;
  }
  .model-name, .tenant-name {
    font-size: 13px;
    color: #262626;
    font-weight: 500;
    margin-top: 2px;
  }
}

// .mono-text {
//   font-family: Menlo, Monaco, Consolas, monospace;
// }

.remove-btn {
  color: #ff4d4f;
  &:hover { color: #ff7875; }
}
</style>
